/**
 * NetworkBanner Component
 *
 * Displays network connectivity status banner
 */

import { useTranslation } from '@/hooks/useTranslation';

interface NetworkBannerProps {
  /** Whether the device is online */
  isOnline: boolean;
  /** Whether we're attempting to reconnect */
  isReconnecting: boolean;
  /** Callback to manually trigger reconnection */
  onReconnect?: () => void;
}

export function NetworkBanner({
  isOnline,
  isReconnecting,
  onReconnect,
}: NetworkBannerProps) {
  const { t } = useTranslation();

  // Don't show anything if online and not reconnecting
  if (isOnline && !isReconnecting) {
    return null;
  }

  return (
    <div
      className={`
        fixed top-0 left-0 right-0 z-[100]
        px-4 py-2
        flex items-center justify-center gap-2
        text-sm font-medium
        transition-all duration-300
        ${isOnline ? 'bg-amber-500 text-amber-900' : 'bg-red-500 text-white'}
      `}
      role="alert"
      aria-live="polite"
    >
      {!isOnline ? (
        <>
          <OfflineIcon className="w-4 h-4" />
          <span>{t('network.offline')}</span>
        </>
      ) : isReconnecting ? (
        <>
          <ReconnectingIcon className="w-4 h-4 animate-spin" />
          <span>{t('network.reconnecting')}</span>
        </>
      ) : null}

      {!isOnline && onReconnect && (
        <button
          onClick={onReconnect}
          className="
            ml-2 px-2 py-1
            bg-white/20 rounded
            hover:bg-white/30
            transition-colors
          "
        >
          {t('network.reconnect')}
        </button>
      )}
    </div>
  );
}

function OfflineIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414"
      />
    </svg>
  );
}

function ReconnectingIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
      />
    </svg>
  );
}
